import { useEffect, useRef } from 'react';
import { Alert, Box, Button, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useLocalization from '../../hooks/useLocalization';
import type { useTurnstile } from '../../hooks/useTurnstile';
import { getTurnstileLanguage, loadTurnstile } from '../../utils/turnstile';

type Props = ReturnType<typeof useTurnstile>['widgetProps'];

const styles = {
  root: { display: 'flex', flexDirection: 'column', gap: 1, width: '100%' },
};

const TurnstileChallenge = ({ siteKey, action, resetKey, error, onVerify, onExpire, onError, retry }: Props) => {
  const container = useRef<HTMLDivElement>(null);
  const { t, i18n } = useLocalization();
  const themeMode = useTheme().palette.mode;
  const language = getTurnstileLanguage(i18n?.resolvedLanguage ?? i18n?.language);
  const isNarrow = useMediaQuery('(max-width:359.95px)');

  useEffect(() => {
    if (!siteKey) {
      return;
    }

    let disposed = false;
    let removeWidget: (() => void) | undefined;

    loadTurnstile()
      .then((api) => {
        if (disposed || !container.current) {
          return;
        }

        const widgetId = api.render(container.current, {
          sitekey: siteKey,
          action,
          theme: themeMode,
          language,
          size: isNarrow ? 'compact' : 'flexible',
          'response-field': false,
          callback: (token) => {
            if (!disposed) {
              onVerify(token);
            }
          },
          'expired-callback': () => {
            if (!disposed) {
              onExpire();
            }
          },
          'error-callback': () => {
            if (!disposed) {
              onError();
            }
          },
          'timeout-callback': () => {
            if (!disposed) {
              onError();
            }
          },
        });

        removeWidget = () => api.remove(widgetId);
      })
      .catch(() => {
        if (!disposed) {
          onError();
        }
      });

    return () => {
      disposed = true;
      removeWidget?.();
      onExpire();
    };
  }, [action, isNarrow, language, onError, onExpire, onVerify, resetKey, siteKey, themeMode]);

  if (!siteKey) {
    return null;
  }

  return (
    <Box sx={styles.root}>
      <div ref={container} />
      {error && (
        <Alert
          severity='error'
          action={
            <Button type='button' onClick={retry}>
              {t('retry')}
            </Button>
          }
        >
          {t(`turnstile.${error}`)}
        </Alert>
      )}
    </Box>
  );
};

export default TurnstileChallenge;
